// A settings page generated from the registry through its layout override (plan §4.7): the legacy layout (heading
// column, controls, rules between rows, Save bottom right), each control chosen by the key's schema, explicit Save
// that sends only the changed keys (`settings.set` per key). Admin-only keys are locked for merchants ("Change
// requires support"); cloud-origin keys are read-only on the location node.
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { Banner, Button, Checkbox, CheckboxSetting, LoadingState, LockedNote, MultiSelectField, RadioGroup, ScaledField, SelectField, SettingRow, SettingsPage, TextField, useToast } from '@radish/ui-web';
import { settings as registry, type SettingKey } from '@radish/contracts';
import type { Rate } from '@radish/primitives';
import { money, type Money } from '@radish/primitives';
import { useActiveLocation } from '../../app/location.tsx';
import { useWritable } from '../../env/availability.tsx';
import { useCommand, useLocationQuery } from '../../data/api.tsx';
import { encodeSetting, SETTINGS_QUERY, useEffectiveSettings } from '../../data/settings.ts';
import { formatHourShort } from '../../lib/format.ts';
import { hundredthsOf, rateFromHundredths, rateFromWholePercent } from '../../lib/rates.ts';
import { PHONE_FULL_WIDTH, PageTabs } from '../../shell/page.tsx';
import { LAYOUTS, type LayoutId } from './layouts.ts';
import { controlOf, dirtyKeys, keysOf, ruleError, type LayoutField, type LayoutSection, type SettingValues } from './registry-form.ts';

const QUARTER_HOURS = Array.from({ length: 96 }, (_, i) => {
  const value = `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`;
  return { value, label: formatHourShort(value) };
});

export function useSetSetting(locationId: string) {
  return useCommand(
    locationId,
    (c, input: { readonly key: SettingKey; readonly value: unknown }, call) =>
      c.commands.settings.set({ scope: { kind: 'location', locationId: locationId as never }, key: input.key, value: input.value }, call),
    [SETTINGS_QUERY],
  );
}

interface FieldProps {
  readonly field: LayoutField;
  readonly value: unknown;
  readonly onChange: (value: unknown) => void;
  readonly disabled: boolean;
  /** A stacked layout's checkbox (legacy BaseCheckbox in a settings stack). */
  readonly stacked?: boolean;
  /** In a `cols` row the box fills its column (no 320 px cap). */
  readonly wide?: boolean;
}

/**
 * Legacy's BaseCheckbox beside a plain label, as a settings stack draws it (Report Settings, measured on the DDEV
 * 2026-10-15): the 40 px q-checkbox, its label 8 px after it in the secondary colour at normal weight, 2 px lower.
 */
function StackedCheckbox({ label, checked, onChange, disabled }: { readonly label: string; readonly checked: boolean; readonly onChange: (v: boolean) => void; readonly disabled: boolean }) {
  const id = useId();
  return (
    <div className="flex items-start gap-2" data-testid="stacked-checkbox">
      <span className="inline-flex size-10 shrink-0 items-center justify-center">
        <Checkbox id={id} label={label} hideLabel checked={checked} onCheckedChange={onChange} disabled={disabled} />
      </span>
      <label htmlFor={id} className="cursor-pointer pt-0.5 text-[14px] text-text-2">{label}</label>
    </div>
  );
}

/** Legacy's labelled BaseCheckbox with its description indented under it (LayoutField `labelledCheckbox`). */
function LabelledCheckbox({ label, description, checked, onChange, disabled }: {
  readonly label: string; readonly description: string | undefined; readonly checked: boolean; readonly onChange: (v: boolean) => void; readonly disabled: boolean;
}) {
  const id = useId();
  return (
    <div data-testid="labelled-checkbox">
      {/* At least 40 px: a label that wraps (Staff Tips' Payroll Tips at 600) grows the row as legacy's q-checkbox does,
          where a fixed 40 px row centred its two lines a pixel high and set the description 2 px early (B1511). */}
      <div className="flex min-h-10 items-center">
        <span className="inline-flex size-10 shrink-0 items-center justify-center">
          <Checkbox id={id} label={label} hideLabel checked={checked} onCheckedChange={onChange} disabled={disabled} />
        </span>
        <label htmlFor={id} className="cursor-pointer text-[14px] leading-[1.5] font-medium text-text-2">{label}</label>
      </div>
      {description === undefined ? null : <p className="pl-10 text-[14px] leading-[1.5] text-text-2" data-testid="labelled-checkbox-description">{description}</p>}
    </div>
  );
}

/**
 * Legacy's value setting under a checkbox setting (LayoutField `underCheckbox`, B1904; RegisterSettings.vue's Auto-print
 * within, measured on the DDEV at 1600 x 996): the words 42 px into the field column, under the checkbox's title (the
 * twin's 11 px inset, its 18 px box and 13 px gap, as `Checkbox settingDescription` draws them), the bold title, 8 px, the
 * description, 16 px, then the dense outlined box 160 px wide in a 60 px slot (Quasar's --with-bottom keeps 20 px under the
 * box whether or not a message shows, and a two-line message overflows it rather than moving the rows below).
 */
/** HTML's valid floating-point number: an optional minus, digits with an optional fraction, an optional exponent. */
const FLOAT = /^-?(?:\d+(?:\.\d+)?|\.\d+)(?:[eE][-+]?\d+)?$/;

/**
 * What legacy's `type="number"` q-input holds for the typed text: the text when the browser reads it as a number, else
 * empty, so "0x10" or "abc" reads as empty (and breaks the whole-number rule) rather than as 16 or a stray value (B1923).
 */
export const numberInputText = (text: string): string => (FLOAT.test(text.trim()) ? text.trim() : '');

function UnderCheckboxField({ title, description, fieldLabel, suffix, text, error, disabled, onText }: {
  readonly title: string; readonly description: string | undefined; readonly fieldLabel: string; readonly suffix: string | undefined;
  readonly text: string; readonly error: string | undefined; readonly disabled: boolean; readonly onText: (text: string) => void;
}) {
  return (
    <div className="ml-[11px] flex items-start gap-[13px]" data-testid="under-checkbox">
      <span aria-hidden className="invisible size-[18px] shrink-0" data-testid="under-checkbox-twin" />
      <div className="min-w-0 flex-1">
        <div className="mb-2 text-[14px] leading-[1.5] font-bold text-text-2">{title}</div>
        {description === undefined ? null : <p className="mb-4 text-[14px] leading-[1.5] text-text-2">{description}</p>}
        <div className="h-[60px]" data-testid="under-checkbox-slot">
          <TextField
            label={fieldLabel} dense inputMode="decimal" value={text} onValueChange={onText} disabled={disabled} error={error}
            frameClassName="max-w-[160px]" {...(suffix === undefined ? {} : { suffix })}
          />
        </div>
      </div>
    </div>
  );
}

/** Legacy TipsCustomAmounts' placeholders, the default presets. */
const TIP_PLACEHOLDERS = ['15', '20', '25'] as const;

function SettingControl({ field, value, onChange, disabled, stacked = false, wide = false }: FieldProps) {
  const control = controlOf(field.key);
  const label = field.label ?? registry[field.key].label;
  switch (control.kind) {
    case 'checkbox':
      if (field.flavour === 'radio') {
        return (
          <RadioGroup<'true' | 'false'>
            label={label}
            disabled={disabled}
            value={value === true ? 'true' : 'false'}
            options={[{ value: 'true', label: field.options?.['true'] ?? 'Yes' }, { value: 'false', label: field.options?.['false'] ?? 'No' }]}
            onValueChange={(v) => onChange(v === 'true')}
            rows="q-option-group"
          />
        );
      }
      if (stacked) return <StackedCheckbox label={label} checked={value === true} onChange={onChange} disabled={disabled} />;
      return <Checkbox label={label} settingDescription checked={value === true} onCheckedChange={onChange} disabled={disabled} {...(field.description === undefined ? {} : { description: field.description })} />;
    case 'choice': {
      const options = control.options.map((o) => ({ value: o, label: field.options?.[o] ?? o.replace(/_/g, ' ') }));
      return field.flavour === 'select'
        ? <SelectField label={label} value={String(value)} options={options} onValueChange={onChange} disabled={disabled} className="max-w-[320px]" />
        : <RadioGroup label={label} value={String(value)} options={options} onValueChange={onChange} disabled={disabled} {...(field.flavour === 'radio' ? { rows: 'q-option-group' as const } : {})} {...(field.plainLabels === true ? { plainLabels: true } : {})} />;
    }
    case 'integer': {
      if (field.options !== undefined) {
        return (
          <SelectField label={label} value={String(value)} options={Object.entries(field.options).map(([v, l]) => ({ value: v, label: l }))}
            onValueChange={(v) => onChange(Number(v))} disabled={disabled} className="max-w-[320px]" />
        );
      }
      const minutes = typeof value === 'number' ? value : 0;
      const text = field.unit === 'hours' ? String(minutes / 60) : String(minutes);
      return (
        <TextField
          label={label} inputMode="decimal" value={text} disabled={disabled} frameClassName="max-w-[320px]"
          hint={field.unit === 'hours' ? `Between ${String(control.min / 60)} and ${String(control.max / 60)} hours` : undefined}
          onValueChange={(t) => {
            const n = Number(t);
            if (t.trim() === '' || Number.isNaN(n)) return;
            onChange(field.unit === 'hours' ? Math.round(n * 60) : Math.trunc(n));
          }}
        />
      );
    }
    case 'percent':
      if (field.wholePercent !== undefined) {
        // Legacy MoneyInput mask "##" (reverse fill, fill-mask 0): two digits, so 0 reads "00", a third digit or a decimal
        // point is refused, and nothing past 99 can be typed (B1949). The empty prefix keeps the value 16 px in (B1945). At 0 it
        // is muted, as MoneyInput's ui-money-input-empty (#2993 review). A non-whole Rate stored before (17.5 %) reads "17";
        // it is only written back when this field is edited, as dirtyKeys sends changed keys alone.
        const whole = hundredthsOf(value as Rate) / 100n;
        return (
          <TextField
            label={label} prefix="" suffix="%" inputMode="numeric" placeholder={field.wholePercent.placeholder} muted={whole === 0n}
            value={String(whole).padStart(2, '0')} disabled={disabled} frameClassName={wide ? 'w-full' : 'max-w-[320px]'}
            onValueChange={(t) => {
              if (/[^\d]/.test(t)) return;
              const digits = t.replace(/^0+(?=\d)/, '');
              if (digits.length > 2) return;
              onChange(rateFromWholePercent(BigInt(digits === '' ? '0' : digits)));
            }}
          />
        );
      }
      return <ScaledField label={label} suffix="%" muteZero decimals={2} value={hundredthsOf(value as Rate)} onValueChange={(v) => onChange(rateFromHundredths(v ?? 0n))} disabled={disabled} frameClassName={wide ? 'w-full' : 'max-w-[320px]'} />;
    case 'money': {
      const m = value as Money;
      return <ScaledField label={label} prefix="$" value={m.minor} onValueChange={(v) => onChange(money(v ?? 0n, m.currency))} disabled={disabled} frameClassName={wide ? 'w-full' : 'max-w-[320px]'} />;
    }
    case 'quarter-hour':
      return <SelectField label={label} value={String(value)} options={QUARTER_HOURS} onValueChange={onChange} disabled={disabled} className="max-w-[320px]" />;
    case 'percent-presets': {
      const presets = Array.isArray(value) ? (value as number[]) : [];
      // Legacy TipsCustomAmounts.vue: a UiSectionHeading (14 px bold on a 22 px line, 12 px over it and 8 under), the description, then
      // three unlabelled MoneyInputs (mask "##", so 0 reads "00", greyed as B807) stacked 8 px apart 16 px down, each a
      // col-sm-3 (a quarter of the column, 120 px at least, full width under 600) with legacy's placeholders (B1505).
      // Each passes MoneyInput's prefix="", which Quasar still draws: an empty q-field__prefix whose 4 px padding sets the value 16 px in (B1945).
      return (
        <div role="group" aria-label={label} data-testid="percent-presets">
          {/* Quasar's text-subtitle2 (a 22 px line, 0.00714em tracking) in bold; .rad-section-heading adds 12 px over it. */}
          <div className="mt-3 mb-2 text-[14px] leading-[22px] font-bold tracking-[0.00714em] text-text-1">{label}</div>
          {field.description === undefined ? null : <p className="text-[14px] leading-[1.5] text-text-2">{field.description}</p>}
          <div className="mt-4 flex flex-col gap-2">
            {Array.from({ length: control.count }, (_, i) => (
              <TextField
                key={i} label={`Option ${String(i + 1)}`} hideLabel prefix="" suffix="%" inputMode="numeric" placeholder={TIP_PLACEHOLDERS[i]}
                frameClassName="w-full min-[600px]:w-1/4 min-[600px]:min-w-[120px]" disabled={disabled}
                muted={(presets[i] ?? 0) === 0}
                value={String(presets[i] ?? 0).padStart(2, '0')}
                onValueChange={(t) => {
                  const n = Number(t.replace(/\D/g, '') || '0');
                  if (n <= 100) onChange(presets.map((p, j) => (j === i ? n : p)));
                }}
              />
            ))}
          </div>
        </div>
      );
    }
    case 'ids':
      return field.choicesFrom === 'dining-options'
        ? <DiningOptionsChoice label={label} hint={field.fieldHint} values={Array.isArray(value) ? (value as string[]) : []} onChange={onChange} />
        : <Banner tone="locked">This setting cannot be edited here yet.</Banner>;
    case 'unsupported':
      return <Banner tone="locked">This setting cannot be edited here yet.</Banner>;
  }
}

/** The location's dining options as chips to pick (legacy AutoGratuitySettings.vue `use-chips`, `multiple`). */
function DiningOptionsChoice({ label, hint, values, onChange }: { readonly label: string; readonly hint: string | undefined; readonly values: readonly string[]; readonly onChange: (value: unknown) => void }) {
  const location = useActiveLocation();
  const dining = useLocationQuery(location.locationId, 'catalog.diningOptions.list', { locationId: location.locationId }, (c) => c.queries.catalog.diningOptions.list({ locationId: location.locationId as never }));
  const options = (dining.data?.diningOptions ?? []).map((d) => ({ value: d.diningOptionId as string, label: d.name }));
  return (
    <div className="max-w-[608px]">
      <MultiSelectField label={label} values={values} options={options} onValuesChange={onChange} {...(hint === undefined ? {} : { hint })} />
    </div>
  );
}

interface GeneratedSettingsPageProps {
  readonly layoutId: LayoutId;
  /** Renders below the Save (a page with its own list). */
  readonly children?: ReactNode;
  /** Renders right under a field, given the unsaved values (Pacing's courses under Enable Courses while it is ticked). */
  readonly belowField?: Partial<Record<SettingKey, (values: SettingValues) => ReactNode>>;
  /** Rows after the generated sections and above the Save (Pacing's Print Options link); each renders its own SettingRow. */
  readonly rows?: ReactNode;
  /** Save spans the page under 600 px, as legacy's ID Verification and Pacing pass BaseButton `full` on isMobile (B1938). */
  readonly phoneFullSave?: boolean;
}

/** A generated page from its layout, with optional extras around the generated fields. */
export function GeneratedSettingsPage({ layoutId, children, belowField, rows, phoneFullSave = false }: GeneratedSettingsPageProps) {
  const layout = LAYOUTS[layoutId];
  const location = useActiveLocation();
  const toast = useToast();
  const effective = useEffectiveSettings(location.locationId);
  const setSetting = useSetSetting(location.locationId);
  const writable = useWritable('cloud');
  const keys = useMemo(() => keysOf(layout), [layout]);
  const loaded = useMemo<SettingValues>(() => {
    const s = effective.settings;
    return s === undefined ? {} : Object.fromEntries([...keys, ...('reads' in layout ? layout.reads : [])].map((k) => [k, s.get(k)]));
  }, [effective.settings, keys, layout]);
  const [values, setValues] = useState<SettingValues>({});
  const [saving, setSaving] = useState(false);
  const [effectiveNote, setEffectiveNote] = useState<string | null>(null);
  // The text typed into an `underCheckbox` field (which may break its rules; the value keeps the last that held), and
  // whether a Save has been refused, which shows every failing rule as legacy's q-form validate() does.
  const [drafts, setDrafts] = useState<Partial<Record<SettingKey, string>>>({});
  const [refused, setRefused] = useState(false);
  useEffect(() => {
    setValues(loaded);
    setDrafts({});
    setRefused(false);
  }, [loaded]);

  const textOf = (key: SettingKey): string => drafts[key] ?? (values[key] === undefined ? '' : String(values[key] as number));
  /** The `underCheckbox` fields on show (a hidden one is not validated, as Quasar's unmounted q-input isn't). */
  const shownRuled = (current: SettingValues): LayoutField[] =>
    layout.sections
      .filter((section) => !('visibleWhen' in section) || section.visibleWhen(current))
      .flatMap((section) => (section.fields as readonly LayoutField[]).filter((f) => f.visibleWhen === undefined || f.visibleWhen(current)))
      .filter((f) => f.underCheckbox !== undefined);
  /**
   * The ruled fields on show that break a rule and that this user can edit: a locked field's stored value (an imported
   * 2000) can't be fixed by them, so it doesn't hold the rest of the page's Save (B1923; legacy has no per-setting lock).
   */
  const failing = (): SettingKey[] =>
    shownRuled(values)
      .filter((f) => f.underCheckbox !== undefined && locked(f.key) === null && ruleError(f.underCheckbox.rules, numberInputText(textOf(f.key))) !== undefined)
      .map((f) => f.key);
  // A field hidden by its checkbox drops its draft, so it shows its value again with no message when it comes back, as
  // legacy's q-input re-mounts on its model (B1923).
  useEffect(() => {
    const shown = new Set(shownRuled(values).map((f) => f.key));
    setDrafts((cur) => (Object.keys(cur).every((k) => shown.has(k as SettingKey)) ? cur : Object.fromEntries(Object.entries(cur).filter(([k]) => shown.has(k as SettingKey)))));
  }, [values, layout]);

  const locked = (key: SettingKey): 'admin' | 'cloud' | 'permission' | null => {
    const def = registry[key];
    if (def.adminOnly && !location.isPlatformAdmin) return 'admin';
    if (def.origin === 'cloud' && !writable) return 'cloud';
    if (!location.can(def.permission)) return 'permission';
    return null;
  };

  const save = async () => {
    if (failing().length > 0) {
      setRefused(true);
      return;
    }
    const dirty = dirtyKeys(loaded, values, keys);
    if (dirty.length === 0) {
      toast.success('Settings saved successfully');
      return;
    }
    setSaving(true);
    try {
      for (const key of dirty) {
        const result = await setSetting.mutateAsync({ key, value: encodeSetting(key, values[key] as never) });
        if (result.effectiveFrom !== null) setEffectiveNote(`Takes effect from the business day starting ${result.effectiveFrom}. Past business days are not changed.`);
      }
      toast.success('Settings saved successfully');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (effective.isPending) return <LoadingState />;
  const anyEditable = keys.some((k) => locked(k) === null);
  return (
    <div data-testid={`settings-${layout.id}`}>
      {'tabs' in layout ? <PageTabs label={'tabsLabel' in layout ? layout.tabsLabel : 'Tips'} tabs={layout.tabs} /> : null}
      <SettingsPage
        {...('intro' in layout ? { intro: layout.intro } : {})}
        top={'tabs' in layout ? 'tabs' : 'page'}
        actions={anyEditable ? <Button onClick={() => void save()} loading={saving} className={phoneFullSave ? PHONE_FULL_WIDTH : undefined}>Save</Button> : undefined}
      >
        {layout.sections.filter((section) => !('visibleWhen' in section) || section.visibleWhen(values)).map((section, i, shown) => {
          const visible = section.fields.filter((f) => !('visibleWhen' in f) || f.visibleWhen(values));
          if ('stacked' in layout && layout.stacked === true) {
            // Legacy's settings stack (24 px apart): a rule, 8 px, the 32 px PageHeader with its 16 px margin, then the
            // description and each field 12 px apart (ReportSettings.vue `.report-settings__section`).
            return (
              <section key={section.heading} aria-label={section.heading} className={`flex flex-col gap-3 border-t border-line pt-2${i === 0 ? '' : ' mt-6'}`}>
                <h3 className="mb-4 text-heading-sm font-bold text-text-1">{section.heading}</h3>
                {'description' in section ? <p className="text-[14px] leading-[1.5] text-text-2">{section.description}</p> : null}
                {visible.map((field) => {
                  const lock = locked(field.key);
                  return (
                    <div key={field.key} data-setting={field.key}>
                      <SettingControl field={field} value={values[field.key]} onChange={(v) => setValues((cur) => ({ ...cur, [field.key]: v }))} disabled={lock !== null} stacked />
                      {lock === 'admin' ? <LockedNote>Change requires support</LockedNote> : null}
                      {lock === 'cloud' ? <LockedNote>Changes online only</LockedNote> : null}
                    </div>
                  );
                })}
                {i === shown.length - 1 && anyEditable ? <hr aria-hidden className="mt-3 border-line" /> : null}
              </section>
            );
          }
          return (
            // Legacy draws a q-separator before Save, so the last row keeps its rule while there is a Save.
            <SettingRow key={section.heading} heading={section.heading} first={i === 0} last={i === shown.length - 1 && rows === undefined && !anyEditable}>
              <div className={`flex flex-col ${(section as LayoutSection).tight === true ? 'gap-2.5' : 'gap-6'}`} data-tight={(section as LayoutSection).tight === true ? '' : undefined}>
                {'description' in section ? <p className="text-[14px] leading-[1.5] text-text-2">{section.description}</p> : null}
                {visible.map((field, f) => {
                  const lock = locked(field.key);
                  // Legacy UiCustomSetting: each field of a section sits under a rule across the field column.
                  const ruled = f > 0 && ((section as LayoutSection).fieldRules === true || !('fieldRules' in layout && layout.fieldRules === false));
                  if ((field as LayoutField).labelledCheckbox === true && controlOf(field.key).kind === 'checkbox') {
                    return (
                      <div key={field.key} className={ruled ? 'border-t border-line pt-6' : ''} data-setting={field.key} data-ruled={ruled ? '' : undefined}>
                        <LabelledCheckbox
                          label={(field as LayoutField).label ?? registry[field.key].label} description={(field as LayoutField).description}
                          checked={values[field.key] === true} onChange={(v) => setValues((cur) => ({ ...cur, [field.key]: v }))} disabled={lock !== null}
                        />
                        {lock === 'admin' ? <LockedNote>Change requires support</LockedNote> : null}
                        {lock === 'cloud' ? <LockedNote>Changes online only</LockedNote> : null}
                        {belowField?.[field.key]?.(values)}
                      </div>
                    );
                  }
                  const under = (field as LayoutField).underCheckbox;
                  if (under !== undefined && controlOf(field.key).kind === 'integer') {
                    const text = textOf(field.key);
                    return (
                      <div key={field.key} className={ruled ? 'border-t border-line pt-6' : ''} data-setting={field.key} data-ruled={ruled ? '' : undefined}>
                        <UnderCheckboxField
                          title={(field as LayoutField).label ?? registry[field.key].label} description={(field as LayoutField).description}
                          fieldLabel={under.fieldLabel} suffix={under.suffix} text={text} disabled={lock !== null}
                          error={drafts[field.key] !== undefined || refused ? ruleError(under.rules, numberInputText(text)) : undefined}
                          onText={(t) => {
                            setDrafts((cur) => ({ ...cur, [field.key]: t }));
                            if (ruleError(under.rules, numberInputText(t)) === undefined) setValues((cur) => ({ ...cur, [field.key]: Number(numberInputText(t)) }));
                          }}
                        />
                        {lock === 'admin' ? <LockedNote>Change requires support</LockedNote> : null}
                        {lock === 'cloud' ? <LockedNote>Changes online only</LockedNote> : null}
                      </div>
                    );
                  }
                  const dense = (field as LayoutField).denseCheckbox;
                  if (dense !== undefined && controlOf(field.key).kind === 'checkbox') {
                    // Legacy UiCustomSetting with a dense box: what shows under the setting sits in its description column.
                    return (
                      <div key={field.key} className={ruled ? 'border-t border-line pt-6' : ''} data-setting={field.key} data-ruled={ruled ? '' : undefined}>
                        <CheckboxSetting
                          label={(field as LayoutField).label ?? registry[field.key].label} titleGap={dense === 'title-gap'}
                          checked={values[field.key] === true} onCheckedChange={(v) => setValues((cur) => ({ ...cur, [field.key]: v }))} disabled={lock !== null}
                          {...((field as LayoutField).description === undefined ? {} : { description: (field as LayoutField).description })}
                        >
                          {lock === 'admin' ? <LockedNote>Change requires support</LockedNote> : null}
                          {lock === 'cloud' ? <LockedNote>Changes online only</LockedNote> : null}
                          {belowField?.[field.key]?.(values)}
                        </CheckboxSetting>
                      </div>
                    );
                  }
                  const cols = (field as LayoutField).cols;
                  if (cols !== undefined) {
                    // Legacy `q-pl-sm row q-col-gutter-md` (B1948): the run of `cols` fields is one row, drawn at its first field.
                    // A `cols` field draws no `hint`, `belowField` or rule; none of Auto Gratuity's three has one.
                    if (f > 0 && (visible[f - 1] as LayoutField).cols !== undefined) return null;
                    const run: LayoutField[] = [];
                    for (let k = f; k < visible.length && (visible[k] as LayoutField).cols !== undefined; k += 1) run.push(visible[k] as LayoutField);
                    return (
                      <div key={field.key} className="grid grid-cols-12 gap-4 pl-2" data-field-row>
                        {run.map((rf) => (
                          <div key={rf.key} className={`col-span-12 flex min-w-0 flex-col gap-2 ${rf.cols === 8 ? 'min-[600px]:col-span-8' : 'min-[600px]:col-span-4'}`} data-setting={rf.key}>
                            <SettingControl field={rf} value={values[rf.key]} onChange={(v) => setValues((cur) => ({ ...cur, [rf.key]: v }))} disabled={locked(rf.key) !== null} wide />
                            {locked(rf.key) === 'admin' ? <LockedNote>Change requires support</LockedNote> : null}
                            {locked(rf.key) === 'cloud' ? <LockedNote>Changes online only</LockedNote> : null}
                          </div>
                        ))}
                      </div>
                    );
                  }
                  return (
                    // A title with legacy's `q-mb-sm` and no description keeps its 8 px: a 29 px row, not the 24 px cell (B2069).
                    <div key={field.key} className={`flex flex-col gap-2${ruled ? ' border-t border-line pt-6' : ''}${(field as LayoutField).titleMargin === true ? ' min-h-[29px]' : ''}`} data-setting={field.key} data-ruled={ruled ? '' : undefined}>
                      <SettingControl field={field} value={values[field.key]} onChange={(v) => setValues((cur) => ({ ...cur, [field.key]: v }))} disabled={lock !== null} />
                      {'hint' in field ? (
                        // Legacy RegisterSettings' `settings-hint text-secondary tw-mt-2.5` (B1944): 13 px italic on a 128.571 % line,
                        // 10 px under the description and in its column (42 px in), centred beside InfoMessageIcon (1.2em, font-tertiary), 6 px apart.
                        <p className="mt-0.5 ml-[42px] flex items-center gap-1.5 text-[13px] leading-[128.571%] italic text-text-2" data-testid={`hint-${field.key}`}>
                          <InfoMessageIcon />
                          <span>{field.hint}</span>
                        </p>
                      ) : null}
                      {lock === 'admin' ? <LockedNote>Change requires support</LockedNote> : null}
                      {lock === 'cloud' ? <LockedNote>Changes online only</LockedNote> : null}
                      {belowField?.[field.key]?.(values)}
                    </div>
                  );
                })}
                {section.fields.some((f) => f.key === 'business_day.start_time') ? (
                  <p className="text-[14px] text-text-2" data-testid="business-day-effect">
                    Sales after {formatHourShort(String(values['business_day.start_time'] ?? '04:00'))} count toward the next business day. A change applies from the next business day that has not started, never to past days.
                  </p>
                ) : null}
              </div>
            </SettingRow>
          );
        })}
        {rows}
        {effectiveNote === null ? null : <Banner testId="effective-note">{effectiveNote}</Banner>}
      </SettingsPage>
      {children}
    </div>
  );
}

/** Legacy components/icons/common/InfoMessageIcon.vue: a speech bubble holding "!", 16 x 16 at 1.2em, in font-tertiary. */
function InfoMessageIcon() {
  return (
    <svg aria-hidden data-testid="info-message-icon" viewBox="0 0 16 16" fill="none" className="size-[1.2em] shrink-0 text-text-3">
      <path
        fill="currentColor"
        d="M10 0H6C2.69 0 0 2.69 0 6C0 7.64 0.685 9.225 1.88 10.355C1.895 10.375 1.915 10.395 1.93 10.41L7.565 15.825C7.685 15.94 7.84 16 8 16C8.085 16 8.165 15.985 8.245 15.95C8.475 15.85 8.625 15.625 8.625 15.375V12H10C13.31 12 16 9.31 16 6C16 2.69 13.31 0 10 0ZM10 10.75H8C7.655 10.75 7.375 11.03 7.375 11.375V13.905L2.87 9.575C2.85 9.55 2.825 9.525 2.8 9.5C1.815 8.6 1.25 7.325 1.25 6C1.25 3.38 3.38 1.25 6 1.25H10C12.62 1.25 14.75 3.38 14.75 6C14.75 8.62 12.62 10.75 10 10.75ZM7.375 6.25V3.24C7.375 2.895 7.655 2.615 8 2.615C8.345 2.615 8.625 2.895 8.625 3.24V6.25C8.625 6.595 8.345 6.875 8 6.875C7.655 6.875 7.375 6.595 7.375 6.25ZM8.875 8.51C8.875 8.995 8.485 9.385 8 9.385C7.515 9.385 7.125 8.995 7.125 8.51C7.125 8.025 7.515 7.635 8 7.635C8.485 7.635 8.875 8.025 8.875 8.51Z"
      />
    </svg>
  );
}
